import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const IMG_PAILLE = "/images/Champ-1.jpeg";
const IMG_LITIERE = "/images/Paille.jpeg";
const IMG_CHAUDIERE = "/images/Chaudière.jpeg";
const IMG_MASSIF = "/images/Massif-paillage.jpg";

const usages = [
  {
    title: "Paillage de jardin et potager",
    avantages: [
      "Régule la température du sol (protection hiver/été)",
      "Limite efficacement les mauvaises herbes",
      "Conserve l'humidité du sol (économie d'eau)",
      "Se décompose lentement (1 à 2 ans), enrichit le sol",
      "Aspect esthétique naturel et aéré",
    ],
    dose: "Épaisseur minimum recommandée : 4 cm pour une efficacité optimale",
    img: IMG_MASSIF,
  },
  {
    title: "Litière pour bovins et volailles",
    avantages: [
      "Résistance mécanique supérieure à la paille de blé",
      "Absorbe l'ammoniaque et les odeurs",
      "Réduction du travail de litière",
      "Parfaite pour les poulaillers et stabulations libres",
      "Compostage rapide après usage",
    ],
    dose: "Adapté à tous types d'élevages bovins, porcins et volailles",
    img: IMG_LITIERE,
  },
  {
    title: "Combustible pour chauffage",
    avantages: [
      "Pouvoir calorifique supérieur (PCI) : environ 4,2 kWh/kg à 15 % d'humidité",
      "Teneur en cendres très faible (< 2 %), limitant l'entretien de la chaudière",
      "Bilan carbone quasi neutre — le CO₂ émis est réabsorbé chaque année par la plante",
      "Production locale, sans intermédiaire",
      "Alternative renouvelable aux combustibles fossiles",
    ],
    dose: "Attention : une chaudière spécifiquement adaptée au miscanthus (biomasse herbacée) est indispensable — renseignez-vous auprès de votre installateur",
    img: IMG_CHAUDIERE,
  },
];

export default function LaPaille() {
  useSEO({
    title: "Paille de miscanthus : paillage, litière, chauffage | Miscanthus Sarthe",
    description: "Paille de miscanthus produite en Sarthe : paillage jardin et potager, litière bovins et volailles 3× plus absorbante, combustible biomasse. Vrac ou sachets 120L.",
    path: "/la-paille",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="paille-page" style={{ backgroundColor: "#f5f2ec" }}>
      {/* Header */}
      <div className="page-header" style={{ paddingTop: "7rem" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-divider mb-4" />
          <h1 className="font-display text-5xl sm:text-6xl mb-5" style={{ color: "#3a2b24" }}>
            La Paille de Miscanthus
          </h1>
          <p className="text-base max-w-2xl" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
            Un paillis naturel d'exception pour vos jardins, potagers et élevages. Produit localement en Sarthe.
          </p>
        </div>
      </div>

      {/* Intro image banner */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img src={IMG_PAILLE} alt="Paille de miscanthus" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(245,242,236,1) 0%, transparent 60%)" }} />
      </div>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3">
              <h2 className="font-display text-3xl sm:text-4xl mb-6" style={{ color: "#3a2b24" }}>
                Le paillis le plus efficace du marché
              </h2>
              <div className="space-y-4 text-base leading-relaxed" style={{ color: "#3a2b24", opacity: 0.8, fontFamily: "'Satoshi', sans-serif" }}>
                <p>
                  Récoltée en mars après le séchage naturel sur pied, la paille de miscanthus possède des propriétés uniques qui en font l'un des meilleurs paillis disponibles sur le marché. Sa structure creuse lui confère une <strong>légèreté et une capacité d'absorption exceptionnelles</strong>.
                </p>
                <p>
                  Contrairement aux paillis d'écorce de pin qui acidifient le sol ou aux paillis de bois frais qui l'appauvrissent en azote, la paille de miscanthus est <strong>neutre pour le pH du sol</strong> et sa décomposition lente enrichit progressivement le sol en matière organique.
                </p>
                <p>
                  Produite localement par l'EARL Torcé Biomasse Energie en Sarthe, elle contribue au développement de l'économie locale et réduit l'empreinte carbone liée au transport.
                </p>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="rounded-xl p-6" style={{ backgroundColor: "#eae4d8", border: "1px solid #d8d1c4" }}>
                <h3 className="font-display text-xl mb-4" style={{ color: "#3a2b24" }}>Tarifs sur devis</h3>
                <p className="text-sm mb-4 leading-relaxed" style={{ color: "#3a2b24", opacity: 0.75, fontFamily: "'Satoshi', sans-serif" }}>
                  Disponible en vrac ou en sachets de 120L. Contactez-nous pour obtenir un tarif personnalisé selon votre volume et votre localisation.
                </p>
                <Link to="/contact" className="btn-primary w-full mt-2 py-3 rounded-md text-sm font-body font-500 flex items-center justify-center gap-2" data-testid="paille-devis-cta">
                  Demander un devis <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Usages */}
      <section style={{ backgroundColor: "#eae4d8" }} className="py-16 px-4 sm:px-6 lg:px-8" data-testid="usages-section">
        <div className="max-w-7xl mx-auto">
          <div className="section-divider mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl mb-12" style={{ color: "#3a2b24" }}>Applications & Utilisations</h2>
          <div className="space-y-8">
            {usages.map((u, i) => (
              <div
                key={i}
                className="rounded-xl overflow-hidden grid grid-cols-1 lg:grid-cols-2"
                style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}
                data-testid={`usage-card-${i}`}
              >
                <div className={`${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <img src={u.img} alt={u.title} loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ minHeight: "240px", maxHeight: "320px" }} />
                </div>
                <div className="p-8">
                  <h3 className="font-display text-2xl mb-4" style={{ color: "#3a2b24" }}>{u.title}</h3>
                  <ul className="space-y-2 mb-4">
                    {u.avantages.map((a, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm" style={{ color: "#3a2b24", fontFamily: "'Satoshi', sans-serif" }}>
                        <CheckCircle2 size={16} style={{ color: "#2d5016", marginTop: "2px", flexShrink: 0 }} />
                        {a}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs mt-2 px-3 py-2 rounded-md inline-block" style={{ backgroundColor: "#eae4d8", color: "#b57a3e", fontFamily: "'Satoshi', sans-serif", fontWeight: 500 }}>
                    Conseil : {u.dose}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="comparison-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-3xl mb-8" style={{ color: "#3a2b24" }}>
            Miscanthus vs. Paille de céréales
          </h2>
          <div className="rounded-xl overflow-hidden" style={{ border: "1px solid #d8d1c4" }}>
            <table className="w-full text-sm" style={{ fontFamily: "'Satoshi', sans-serif" }}>
              <thead>
                <tr style={{ backgroundColor: "#2d5016", color: "white" }}>
                  <th className="text-left px-4 py-3 font-500">Critère</th>
                  <th className="text-center px-4 py-3 font-500">Miscanthus</th>
                  <th className="text-center px-4 py-3 font-500">Paille céréales</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Pouvoir absorbant", "3× supérieur", "Référence"],
                  ["Poussière", "Très faible", "Élevée"],
                  ["Durée de paillis", "18 à 36 mois", "18 à 36 mois"],
                  ["Impact pH du sol", "Neutre", "Légèrement alcalin"],
                  ["Production locale", "Sarthe (local)", "Variable"],
                  ["Agriculture bio", "Sans pesticides", "Variable"],
                ].map(([crit, misc, paille], i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "white" : "#f5f2ec" }}>
                    <td className="px-4 py-3" style={{ color: "#3a2b24", fontWeight: 500 }}>{crit}</td>
                    <td className="px-4 py-3 text-center" style={{ color: "#2d5016", fontWeight: 600 }}>{misc}</td>
                    <td className="px-4 py-3 text-center" style={{ color: "#3a2b24", opacity: 0.6 }}>{paille}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: "#2d5016" }} className="py-12 px-4 text-center">
        <h2 className="font-display text-3xl mb-4" style={{ color: "#f5f2ec" }}>Commander de la paille de miscanthus</h2>
        <p className="text-sm mb-6 opacity-75" style={{ color: "#eae4d8", fontFamily: "'Satoshi', sans-serif" }}>
          Livraison possible en Sarthe et Pays de la Loire. Devis sur demande pour les gros volumes.
        </p>
        <Link to="/contact" className="btn-secondary inline-flex items-center gap-2 px-8 py-3.5 rounded-md font-body font-500 text-base" data-testid="paille-final-cta">
          Demander un devis <ArrowRight size={18} />
        </Link>
      </section>
    </main>
  );
}
